import React, { useState } from 'react';
import { Home, Users as UsersIcon, ClipboardCheck, Flag, ChevronRight } from '../../components/CustomIcons';
import { Btn, Chip, Mono, ProgressBar, ScreenHeader } from '../Prototype';
import { StatCard, SectionTitle, StaffShell, Badge, useUI } from './shared';
import { IN_IDENTITY, LEARNERS, IN_ASSESSMENT } from './data';

const NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: Home },
  { key: 'roster', label: 'Roster', icon: UsersIcon },
  { key: 'assessments', label: 'Assessments', icon: ClipboardCheck },
];

const useINState = () => {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem('assistivos_instructor_state');
      if (raw) return JSON.parse(raw);
    } catch (_e) { /* ignore */ }
    return { tab: 'dashboard', selectedLearnerId: null, flags: {}, notes: {} };
  });
  const update = (patch) => setState((s) => {
    const next = { ...s, ...(typeof patch === 'function' ? patch(s) : patch) };
    try { localStorage.setItem('assistivos_instructor_state', JSON.stringify(next)); } catch (_e) { /* ignore */ }
    return next;
  });
  return [state, update];
};

const IN1_Dashboard = ({ update }) => (
  <>
    <ScreenHeader title="Instructor" subtitle={IN_IDENTITY.name} />
    <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
      <section aria-label="Module summary" className="space-y-3">
        <SectionTitle right={`${IN_IDENTITY.activeLearners} learners`}>My Active Modules</SectionTitle>
        {[
          { m: 'HVAC Fundamentals', e: 18, avg: 54, comp: 3 },
          { m: 'AI Foundations', e: 13, avg: 47, comp: 2 },
        ].map((mod) => (
          <div key={mod.m} className="rounded-[12px] bg-[#f1f5f9] p-4">
            <div className="p-display text-[17px]" style={{ color: '#0f2744' }}>{mod.m}</div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-[12px]">
              <div><div className="text-[#64748b] uppercase tracking-widest font-semibold text-[10px]">Learners</div><div className="mt-0.5"><Mono>{mod.e}</Mono></div></div>
              <div><div className="text-[#64748b] uppercase tracking-widest font-semibold text-[10px]">Avg Progress</div><div className="mt-0.5"><Mono>{mod.avg}</Mono>%</div></div>
              <div><div className="text-[#64748b] uppercase tracking-widest font-semibold text-[10px]">Completions</div><div className="mt-0.5"><Mono>{mod.comp}</Mono></div></div>
            </div>
            <div className="mt-3"><ProgressBar value={mod.avg} /></div>
            <div className="mt-3 flex gap-2">
              <Btn variant="ghost" onClick={() => update({ tab: 'roster' })} data-testid="in-view-roster">View Roster</Btn>
            </div>
          </div>
        ))}
      </section>
      <section aria-label="Recent assessments">
        <SectionTitle>Recent Assessment Results</SectionTitle>
        <button
          onClick={() => update({ tab: 'assessments' })}
          data-testid="in-view-assessment"
          className="w-full text-left rounded-[12px] bg-[#f1f5f9] p-4 hover:bg-white border border-[#e2e8f0] hover:border-[#0f2744]"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[14px] font-semibold text-[#0f2744]">AI Foundations — Knowledge Check {IN_ASSESSMENT.checkNumber}</div>
              <div className="text-[12px] text-[#64748b] mt-0.5">Average <Mono>{IN_ASSESSMENT.avgScore}</Mono>% · <Mono>{IN_ASSESSMENT.belowThreshold.length}</Mono> below 60%</div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#94a3b8]" aria-hidden="true" />
          </div>
        </button>
      </section>
    </main>
  </>
);

const IN2_Roster = ({ update }) => {
  const [filter, setFilter] = useState('all');
  const rows = LEARNERS.filter((l) => l.moduleId === 'hvac');
  const filtered = rows.filter((l) => filter === 'all' || (filter === 'behind' && l.kcAvg < 70) || (filter === 'on-track' && l.kcAvg >= 70 && l.progress < 100) || (filter === 'completed' && l.progress === 100));
  return (
    <>
      <ScreenHeader title="HVAC Fundamentals" subtitle={`${rows.length} learners`} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-3">
        <div className="flex gap-2 flex-wrap" role="tablist">
          {[
            { k: 'all', l: 'All' },
            { k: 'behind', l: 'Behind' },
            { k: 'on-track', l: 'On Track' },
            { k: 'completed', l: 'Completed' },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              role="tab"
              aria-selected={filter === f.k}
              data-testid={`in-filter-${f.k}`}
              className="rounded-full px-3 py-1.5 text-[13px] font-semibold"
              style={filter === f.k ? { background: '#f97316', color: '#0f2744' } : { background: '#f1f5f9', color: '#64748b' }}
            >{f.l}</button>
          ))}
        </div>
        <ul className="list-none p-0 space-y-2">
          {filtered.map((l) => (
            <li key={l.id}>
              <button
                onClick={() => update({ selectedLearnerId: l.id, view: 'detail' })}
                data-testid={`in-learner-${l.id}`}
                className="w-full text-left rounded-[12px] bg-[#f1f5f9] hover:bg-white border border-[#e2e8f0] hover:border-[#0f2744] p-4"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="p-display text-[16px] flex-1" style={{ color: '#0f2744' }}>{l.firstName} {l.lastInitial}</div>
                  <Badge tone={l.progress === 100 ? 'success' : l.kcAvg < 70 ? 'orange' : 'navy'}>
                    {l.progress === 100 ? 'Completed' : l.kcAvg < 70 ? 'Behind' : 'On Track'}
                  </Badge>
                </div>
                <div className="mt-2"><ProgressBar value={l.progress} /></div>
                <div className="mt-1 flex items-center justify-between text-[12px] text-[#64748b]">
                  <span><Mono>{l.progress}</Mono>% · KC avg <Mono>{l.kcAvg}</Mono>%</span>
                  <span>{l.lastActivity}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </main>
    </>
  );
};

const IN3_LearnerDetail = ({ state, update }) => {
  const l = LEARNERS.find((x) => x.id === state.selectedLearnerId);
  const { showModal, showToast } = useUI();
  const [flagNote, setFlagNote] = useState('');
  if (!l) return null;
  const flagLearner = () => showModal({
    title: `Flag ${l.firstName} ${l.lastInitial} for case manager review?`,
    body: 'A note will be sent to the assigned case manager.',
    input: { placeholder: 'What should the case manager know?', value: flagNote, onChange: setFlagNote },
    confirmLabel: 'Send Flag',
    onConfirm: () => {
      update((s) => ({ flags: { ...s.flags, [l.id]: flagNote || 'General flag' } }));
      showToast('Flag sent to Maya Reyes (Case Manager).', 'success');
      setFlagNote('');
    },
  });
  return (
    <>
      <ScreenHeader
        title={`${l.firstName} ${l.lastInitial}`}
        subtitle={`${l.module} · KC avg ${l.kcAvg}%`}
        onBack={() => update({ view: null })}
        breadcrumbs={[
          { label: 'Dashboard', onClick: () => update({ tab: 'dashboard', view: null }) },
          { label: 'Roster', onClick: () => update({ tab: 'roster', view: null }) },
          { label: `${l.firstName} ${l.lastInitial}` },
        ]}
      />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
        <section aria-label="Progress ring" className="rounded-[12px] p-5 text-center" style={{ background: 'linear-gradient(140deg,#0f2744,#081827)', color: '#fafaf9' }}>
          <div className="p-mono text-[38px] leading-none">{l.progress}%</div>
          <div className="text-[12px] mt-1 uppercase tracking-widest" style={{ color: '#f97316' }}>Module Progress</div>
        </section>
        <section aria-label="Lesson breakdown">
          <SectionTitle>Lesson-by-Lesson</SectionTitle>
          <ol className="list-none p-0 space-y-2">
            {l.lessons.map((les, i) => (
              <li key={i} className="rounded-[12px] bg-[#f1f5f9] p-3 flex items-center gap-3">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold" style={{
                  background: les.status === 'complete' ? '#15803d' : les.status === 'in-progress' ? '#f97316' : '#e2e8f0',
                  color: les.status === 'locked' ? '#94a3b8' : '#fafaf9',
                }} aria-hidden="true">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-[#0f2744] truncate">{les.title}</div>
                  <div className="text-[12px] text-[#64748b] mt-0.5">
                    {les.status === 'complete' && <span>Complete · Score <Mono>{les.score}</Mono>%</span>}
                    {les.status === 'in-progress' && <span>In progress</span>}
                    {les.status === 'locked' && <span>Locked</span>}
                  </div>
                </div>
                {les.status === 'complete' && les.score < 70 && <Badge tone="orange">Below avg</Badge>}
              </li>
            ))}
          </ol>
        </section>
        <section aria-label="Insights" className="rounded-[12px] bg-[rgba(15,39,68,0.06)] p-4">
          <SectionTitle>Insights</SectionTitle>
          <p className="text-[14px] text-[#0f2744] leading-relaxed">
            {l.kcAvg < 70
              ? `This learner is trending below the module average. Review Lesson 2 content and consider a 1:1 session.`
              : `Steady pace. Estimated completion in 3 weeks at current cadence.`}
          </p>
        </section>
        <section className="grid grid-cols-2 gap-2">
          <Btn variant="ghost" onClick={flagLearner} data-testid="in-flag">
            <Flag className="w-4 h-4" aria-hidden="true" /> Flag for Case Manager
          </Btn>
          <Btn variant="ghost" onClick={() => showToast('Instructor note saved.')} data-testid="in-add-note">Add Note</Btn>
        </section>
      </main>
    </>
  );
};

const IN4_Assessment = ({ update }) => {
  const { showToast } = useUI();
  const [expanded, setExpanded] = useState(null);
  const belowLearners = LEARNERS.filter((l) => IN_ASSESSMENT.belowThreshold.includes(l.id));
  return (
    <>
      <ScreenHeader title={`Knowledge Check ${IN_ASSESSMENT.checkNumber}`} subtitle={IN_ASSESSMENT.moduleName} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
        <section aria-label="Assessment stats" className="grid grid-cols-3 gap-2">
          <div className="rounded-[12px] bg-[#f1f5f9] p-3 text-center">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">Average</div>
            <div className="p-mono text-[22px] mt-1" style={{ color: '#0f2744' }}>{IN_ASSESSMENT.avgScore}%</div>
          </div>
          <div className="rounded-[12px] bg-[#f1f5f9] p-3 text-center">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">Highest</div>
            <div className="p-mono text-[22px] mt-1" style={{ color: '#15803d' }}>{IN_ASSESSMENT.high}%</div>
          </div>
          <div className="rounded-[12px] bg-[#f1f5f9] p-3 text-center">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">Lowest</div>
            <div className="p-mono text-[22px] mt-1" style={{ color: '#c2410c' }}>{IN_ASSESSMENT.low}%</div>
          </div>
        </section>
        <section aria-label="Question breakdown">
          <SectionTitle>Question Breakdown</SectionTitle>
          <ul className="list-none p-0 space-y-2">
            {IN_ASSESSMENT.questions.map((q, i) => (
              <li key={i} className="rounded-[12px] bg-[#f1f5f9] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-[14px] font-semibold text-[#0f2744] flex-1">{q.text}</div>
                  <div className="p-mono text-[16px] shrink-0" style={{ color: q.correctPct >= 70 ? '#15803d' : '#c2410c' }}>{q.correctPct}%</div>
                </div>
                <div className="text-[12px] text-[#64748b] mt-1">Most common wrong: {q.commonWrong}</div>
                <button
                  onClick={() => setExpanded(expanded === i ? null : i)}
                  data-testid={`in-review-${i}`}
                  className="mt-2 text-[13px] font-semibold text-[#0f2744] hover:underline"
                >
                  {expanded === i ? 'Hide review' : 'Review this question →'}
                </button>
                {expanded === i && (
                  <div className="mt-2 rounded-[8px] bg-white p-3 border border-[#e2e8f0]">
                    <div className="text-[13px] text-[#15803d] font-semibold">Correct: {q.correctAnswer}</div>
                    <div className="text-[13px] text-[#1c1c1e] mt-1">{q.explanation}</div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
        <section aria-label="Learners below threshold">
          <SectionTitle right={`${belowLearners.length} below 60%`}>At-Risk on This Check</SectionTitle>
          <ul className="list-none p-0 space-y-2">
            {belowLearners.map((l) => (
              <li key={l.id} className="rounded-[12px] bg-[#f1f5f9] p-3 flex items-center gap-3">
                <div className="flex-1"><div className="text-[14px] font-semibold text-[#0f2744]">{l.firstName} {l.lastInitial}</div><div className="text-[12px] text-[#64748b]">Score <Mono>{l.kcAvg}</Mono>%</div></div>
                <button onClick={() => showToast(`Flag sent for ${l.firstName} ${l.lastInitial}.`)} data-testid={`in-flag-${l.id}`} className="text-[13px] font-semibold text-[#c2410c] px-3 py-2 rounded-[8px] hover:bg-[rgba(249,115,22,0.10)]">Flag</button>
              </li>
            ))}
          </ul>
          <Btn variant="ghost" onClick={() => showToast(`${belowLearners.length} learners flagged for case manager review.`, 'success')} className="mt-3 w-full" data-testid="in-flag-all">Flag All for Case Manager</Btn>
        </section>
      </main>
    </>
  );
};

const Instructor = () => {
  const [state, update] = useINState();
  const tab = state.tab || 'dashboard';
  const view = state.view;
  const activeKey = view === 'detail' ? 'roster' : tab;
  const onNav = (k) => update({ tab: k, view: null });

  let screen;
  if (view === 'detail') screen = <IN3_LearnerDetail state={state} update={update} />;
  else if (tab === 'roster') screen = <IN2_Roster update={update} />;
  else if (tab === 'assessments') screen = <IN4_Assessment update={update} />;
  else screen = <IN1_Dashboard update={update} />;

  return (
    <StaffShell items={NAV} activeKey={activeKey} onChange={onNav} badge={{ initial: 'K', name: IN_IDENTITY.name, role: 'Instructor' }}>
      {screen}
    </StaffShell>
  );
};

export default Instructor;
